import React, { useState } from 'react';
import api from '../services/api';
import formService from '../services/formService';
import { useTranslation } from 'react-i18next';

export default function PublicMemberRegistration() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    salutation: '',
    chi_name: '',
    eng_name: '',
    mobile: '',
    email: '',
    address_tc: '',
    date_of_birth: '',
    age: '',
    student_grade: '',
    adult_options: [],
    response_choice: '',
    other_text: '',
    reference_person: ''
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'adult_options') {
      setFormData(prev => ({
        ...prev,
        adult_options: checked
          ? [...new Set([...(prev.adult_options || []), value])]
          : (prev.adult_options || []).filter(v => v !== value)
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Validate required fields
      if (!formData.chi_name.trim()) throw new Error('Please enter your name');
      if (!formData.mobile.trim()) throw new Error('Please enter your mobile number');
      if (!formData.email.trim()) throw new Error('Please enter your email');
      if (!formData.date_of_birth.trim()) throw new Error('Please select your date of birth');
      if (!formData.age.trim()) throw new Error('Please enter your age');
      const parsedAge = parseInt(formData.age, 10);
      if (Number.isNaN(parsedAge) || parsedAge < 0) throw new Error('Please enter a valid age');
      if (!consent) throw new Error('Please agree to the privacy notice');

      // Submit to public members register (required minimal fields)
      const memberPayload = {
        chi_name: formData.chi_name,
        mobile: formData.mobile,
        email: formData.email,
        date_of_birth: formData.date_of_birth,
        age: parsedAge,
        reference_person: formData.reference_person || undefined
      };
      await api.post('/api/v1/members/public/register', memberPayload);

      // Attempt to submit extended response to forms slug (optional)
      try {
        await formService.submitBySlug('guestresponse', {
          data: {
            salutation: formData.salutation,
            chi_name: formData.chi_name,
            eng_name: formData.eng_name,
            mobile: formData.mobile,
            email: formData.email,
            address_tc: formData.address_tc,
            date_of_birth: formData.date_of_birth,
            age: parsedAge,
            student_grade: formData.student_grade,
            adult_options: formData.adult_options,
            response_choice: formData.response_choice,
            other_text: formData.other_text,
            consent: true
          }
        });
      } catch (_) {
        // Ignore if form slug not available or submission fails
      }
      
      setSuccess(true);
      setSubmitMessage('Thank you! Your registration has been received.');
      
      // Reset form
      setFormData({
        salutation: '', chi_name: '', eng_name: '', mobile: '', email: '', address_tc: '',
        date_of_birth: '', age: '', student_grade: '', adult_options: [], response_choice: '', other_text: '', reference_person: ''
      });
      setConsent(false);

      // Scroll to success message
      window.scrollTo(0, 0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = Array.isArray(detail)
        ? detail.map(d => d.msg || String(d)).join('; ')
        : (typeof detail === 'string' ? detail : (err.message || 'Registration failed'));
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-1">
            Member Registration
          </h1>
          <h2 className="text-2xl font-bold text-gray-700 mb-4">
            會友登記
          </h2>
          <p className="text-gray-600">
            Please fill in your details to register as a member
          </p>
          <p className="text-gray-600">
            請填寫您的資料以登記成為會友
          </p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <h3 className="font-semibold text-green-800">Success! 成功！</h3>
            </div>
            <p className="text-green-700">Thank you! Your registration has been received.</p>
            <p className="text-green-700">感謝您！我們已收到您的登記。</p>
            <p className="text-sm text-green-600 mt-2">
              You will be contacted by our admin team to complete your membership upgrade.
            </p>
            <p className="text-sm text-green-600">
              我們的同工會聯絡您以完成會籍確認。
            </p>
          </div>
        )}

        {/* Error Message */}
        {!success && error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <h3 className="font-semibold text-red-800">Error 錯誤</h3>
            </div>
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Form */}
        {!success && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Salutation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salutation</label>
            <label className="block text-sm font-medium text-gray-600 mb-2">稱謂</label>
            <select
              name="salutation"
              value={formData.salutation}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              style={{ appearance: 'none', lineHeight: '1.4', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%236c757d'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: '32px' }}
              disabled={loading}
            >
              <option value="">{t('common.na') || 'N/A'}</option>
              <option value="Mr">Mr 先生</option>
              <option value="Ms">Ms 女士</option>
              <option value="Miss">Miss 小姐</option>
            </select>
          </div>
          {/* Required Name Field */}
          <div>
            <label htmlFor="chi_name" className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <label htmlFor="chi_name" className="block text-sm font-medium text-gray-600 mb-2">
              姓名 *
            </label>
            <input
              type="text"
              id="chi_name"
              name="chi_name"
              value={formData.chi_name}
              onChange={handleChange}
              placeholder="Enter your name / 請輸入姓名"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
              required
            />
            <p className="text-sm text-gray-500 mt-1">Required field 必填欄位</p>
          </div>

          {/* English Name */}
          <div>
            <label htmlFor="eng_name" className="block text-sm font-medium text-gray-700 mb-1">English Name</label>
            <label htmlFor="eng_name" className="block text-sm font-medium text-gray-600 mb-2">英文姓名</label>
            <input
              type="text"
              id="eng_name"
              name="eng_name"
              value={formData.eng_name}
              onChange={handleChange}
              placeholder="Enter your English name / 請輸入英文姓名"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          {/* Required Mobile Field */}
          <div>
            <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number *
            </label>
            <label htmlFor="mobile" className="block text-sm font-medium text-gray-600 mb-2">
              手機號碼 *
            </label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              placeholder="Enter your mobile number / 請輸入手機號碼"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
              required
            />
            <p className="text-sm text-gray-500 mt-1">Required field 必填欄位</p>
          </div>

          {/* Required Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <label htmlFor="email" className="block text-sm font-medium text-gray-600 mb-2">
              電郵地址 *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email / 請輸入電郵地址"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
              required
            />
            <p className="text-sm text-gray-500 mt-1">Required field 必填欄位</p>
          </div>

          {/* Address (Chinese) */}
          <div>
            <label htmlFor="address_tc" className="block text-sm font-medium text-gray-700 mb-1">Address (Chinese)</label>
            <label htmlFor="address_tc" className="block text-sm font-medium text-gray-600 mb-2">住址(中文)</label>
            <input
              type="text"
              id="address_tc"
              name="address_tc"
              value={formData.address_tc}
              onChange={handleChange}
              placeholder="請輸入住址(中文)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          {/* Required Date of Birth Field */}
          <div>
            <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth *
            </label>
            <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-600 mb-2">
              出生日期 *
            </label>
            <input
              type="date"
              id="date_of_birth"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
              required
            />
            <p className="text-sm text-gray-500 mt-1">Required field 必填欄位</p>
          </div>

          {/* Required Age Field */}
          <div>
            <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
              Age *
            </label>
            <label htmlFor="age" className="block text-sm font-medium text-gray-600 mb-2">
              年齡 *
            </label>
            <input
              type="number"
              id="age"
              name="age"
              value={formData.age}
              onChange={handleChange}
              placeholder="Enter your age / 請輸入年齡"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
              required
            />
            <p className="text-sm text-gray-500 mt-1">Required field 必填欄位</p>
          </div>

          {/* Student Grade (if applicable) */}
          <div>
            <label htmlFor="student_grade" className="block text-sm font-medium text-gray-700 mb-1">Student Grade</label>
            <label htmlFor="student_grade" className="block text-sm font-medium text-gray-600 mb-2">學生適用(就讀年級)</label>
            <input
              type="text"
              id="student_grade"
              name="student_grade"
              value={formData.student_grade}
              onChange={handleChange}
              placeholder="如：P5、F3、DSE"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          {/* Adult Options (checkboxes) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adult Options</label>
            <label className="block text-sm font-medium text-gray-600 mb-2">成人適用</label>
            <div className="space-y-2">
              {[
                { key: 'baptism', label: '受浸 / Baptism' },
                { key: 'cell_group', label: '加入小組 / Join Cell Group' },
                { key: 'serve', label: '參與服事 / Serve' }
              ].map(opt => (
                <label key={opt.key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="adult_options"
                    value={opt.key}
                    checked={(formData.adult_options || []).includes(opt.key)}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Immediate Response after Service */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Immediate Response</label>
            <label className="block text-sm font-medium text-gray-600 mb-2">參加完今天的崇拜聚會，你有何即時回應：</label>
            <div className="space-y-2">
              {[
                { key: 'believe', label: '決志信主 / Believe in Jesus' },
                { key: 'know_more', label: '想多了解信仰 / Want to know more' },
                { key: 'prayer', label: '代禱需要 / Prayer request' }
              ].map(opt => (
                <label key={opt.key} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="response_choice"
                    value={opt.key}
                    checked={formData.response_choice === opt.key}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Other text */}
          <div>
            <label htmlFor="other_text" className="block text-sm font-medium text-gray-700 mb-1">Other</label>
            <label htmlFor="other_text" className="block text-sm font-medium text-gray-600 mb-2">其他：</label>
            <textarea
              id="other_text"
              name="other_text"
              value={formData.other_text}
              onChange={handleChange}
              rows={3}
              placeholder="可輸入其他事項"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          {/* Consent */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <label className="flex items-start space-x-2">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={loading}
              />
              <span className="text-sm text-gray-700">
                以確保你的個人資料絕對保密，資料只供本會與你聯絡及寄發本會資訊之用，本會絕不會向外披露任何你的任何個人資料。
              </span>
            </label>
          </div>

          {/* Optional Reference Person Field */}
          <div>
            <label htmlFor="reference_person" className="block text-sm font-medium text-gray-700 mb-1">
              Reference Person (optional)
            </label>
            <label htmlFor="reference_person" className="block text-sm font-medium text-gray-600 mb-2">
              介紹人 (選填)
            </label>
            <input
              type="text"
              id="reference_person"
              name="reference_person"
              value={formData.reference_person}
              onChange={handleChange}
              placeholder="Enter reference person / 請輸入介紹人 (選填)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting... 提交中...
              </>
            ) : (
              <>Submit Registration 提交登記</>
            )}
          </button>
        </form>
        )}

        {/* Footer */}
        <div className="mt-8 text-center border-t pt-6">
          <p className="text-sm text-gray-600">
            Your submission will be reviewed by our admin team.
          </p>
          <p className="text-sm text-gray-600">
            您的登記將由我們的同工審核。
          </p>
          <p className="text-sm text-gray-600 mt-2">
            We will contact you shortly.
          </p>
          <p className="text-sm text-gray-600">
            我們會盡快與您聯絡。
          </p>
        </div>
      </div>
    </div>
  );
}
